import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@shared/lib/apiClient';
import { useGlobalScope } from '@shared/context/GlobalScopeContext';
import { useActiveOrganization } from '@shared/context/ActiveOrganizationContext';
export function usePortfolio() {
    const { scope } = useGlobalScope();
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    return useQuery({
        queryKey: ['investments', activeOrganizationId, 'portfolio', scope],
        enabled: orgQueryReady,
        queryFn: async () => {
            const { data } = await apiClient.get(`/investments/portfolio`, {
                params: { userId: scope },
            });
            return data;
        },
    });
}
export function useInvestmentPeriodSummary(period) {
    const { scope } = useGlobalScope();
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    return useQuery({
        queryKey: ['investments', activeOrganizationId, 'period-summary', scope, period],
        enabled: orgQueryReady,
        queryFn: async () => {
            const { data } = await apiClient.get('/investments/period-summary', {
                params: {
                    period,
                    ...(scope === 'all' ? {} : { userId: scope }),
                },
            });
            return data;
        },
    });
}
export function useInvestmentMovements() {
    const { scope } = useGlobalScope();
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    return useQuery({
        queryKey: ['investments', activeOrganizationId, 'movements', scope],
        enabled: orgQueryReady,
        queryFn: async () => {
            const { data } = await apiClient.get('/investments/movements', {
                params: { userId: scope }
            });
            return data;
        },
    });
}
export function useCreateInvestmentMovement() {
    const qc = useQueryClient();
    const { activeOrganizationId } = useActiveOrganization();
    return useMutation({
        mutationFn: async (payload) => {
            const { data } = await apiClient.post('/investments/movements', payload);
            return data;
        },
        onSuccess: () => {
            if (activeOrganizationId) {
                void qc.invalidateQueries({ queryKey: ['investments', activeOrganizationId] });
            }
        },
    });
}
export function useDeleteInvestmentMovement() {
    const qc = useQueryClient();
    const { activeOrganizationId } = useActiveOrganization();
    return useMutation({
        mutationFn: async (id) => {
            await apiClient.delete(`/investments/movements/${id}`);
        },
        onSuccess: () => {
            if (activeOrganizationId) {
                void qc.invalidateQueries({ queryKey: ['investments', activeOrganizationId] });
            }
        },
    });
}
export function usePerformanceMetrics() {
    const { scope } = useGlobalScope();
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    return useQuery({
        queryKey: ['investments', activeOrganizationId, 'metrics', scope],
        enabled: orgQueryReady,
        queryFn: async () => {
            const { data } = await apiClient.get(`/investments/analytics`, {
                params: { userId: scope },
            });
            return data;
        },
    });
}
